/** Serverzustand der Berichte. */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createReportSchedule,
  createTile,
  deleteReportSchedule,
  deleteTile,
  downloadReport,
  fetchDashboard,
  listReports,
  getSurveySummary,
  listReportSchedules,
  listTiles,
  runReport,
  sendSurveysNow,
  type ReportFilters,
} from './api';
import { saveDownload } from '../tickets/articles/attachmentFile';

export const reportKeys = {
  all: ['reporting'] as const,
  catalog: ['reporting', 'catalog'] as const,
  report: (key: string, axis: string | undefined, filters: ReportFilters) =>
    ['reporting', 'report', key, axis ?? '', filters] as const,
  dashboard: ['reporting', 'dashboard'] as const,
  tiles: ['reporting', 'tiles'] as const,
  surveys: ['reporting', 'surveys'] as const,
  schedules: ['reporting', 'schedules'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/** Der Katalog aendert sich nur mit einer neuen Fassung der Anwendung. */
export function useReportCatalog() {
  return useQuery({
    queryKey: reportKeys.catalog,
    queryFn: listReports,
    staleTime: 60 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Ein Bericht.
 *
 * ``enabled`` gibt es fuer die Startseite: Dort haengen drei Diagramme an
 * drei Berichten, und wer eines abgewaehlt hat, soll seine Abfrage auch
 * nicht bezahlen. Ohne den Schalter liefe sie mit und das Ergebnis landete
 * ungesehen im Zwischenspeicher.
 */
export function useReport(
  key: string,
  axis: string | undefined,
  filters: ReportFilters,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: reportKeys.report(key, axis, filters),
    queryFn: () => runReport(key, axis, filters),
    // Beim Wechsel der Achse bleibt die alte Tabelle stehen, statt zu
    // flackern -- die neue kommt an derselben Stelle.
    placeholderData: keepPreviousData,
    retry: retryUnlessPermanent,
    enabled: options.enabled ?? true,
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: reportKeys.dashboard,
    queryFn: fetchDashboard,
    // Eine Minute: Die Startseite steht offen, waehrend gearbeitet wird.
    staleTime: 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useDashboardTiles() {
  return useQuery({
    queryKey: reportKeys.tiles,
    queryFn: listTiles,
    retry: retryUnlessPermanent,
  });
}

export function useSurveySummary() {
  return useQuery({
    queryKey: reportKeys.surveys,
    queryFn: getSurveySummary,
    retry: retryUnlessPermanent,
  });
}

function useTileMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reportKeys.tiles });
    },
  });
}

export function useCreateTile() {
  return useTileMutation((payload: Record<string, unknown>) => createTile(payload));
}

export function useDeleteTile() {
  return useTileMutation((tileId: string) => deleteTile(tileId));
}

export function useReportSchedules() {
  return useQuery({
    queryKey: reportKeys.schedules,
    queryFn: listReportSchedules,
    retry: retryUnlessPermanent,
  });
}

export function useCreateReportSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createReportSchedule(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reportKeys.schedules });
    },
  });
}

export function useDeleteReportSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) => deleteReportSchedule(scheduleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reportKeys.schedules });
    },
  });
}

/** Alle wartenden Umfragen sofort verschicken -- F5-10. */
export function useSendSurveysNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendSurveysNow,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reportKeys.surveys });
    },
  });
}

/**
 * Holt einen Bericht als CSV oder PDF und uebergibt ihn dem Browser -- F5-08.
 *
 * Als Mutation, damit ein gescheiterter Abruf zu sehen ist und der Knopf
 * nicht einfach nichts tut.
 */
export function useReportExport() {
  return useMutation({
    mutationFn: ({
      key,
      axis,
      filters,
      format,
    }: {
      key: string;
      axis: string | undefined;
      filters: ReportFilters;
      format: 'csv' | 'pdf';
    }) =>
      saveDownload(() => downloadReport(key, axis, filters, format), `bericht-${key}.${format}`),
  });
}
